import React, {useEffect, useRef} from 'react';
import {clamp as _clamp, round as _round} from 'lodash';
import {extractNumericUnitAndValue} from 'rendition-core';
import {useViewContext} from 'Contexts/ViewContext';
import styled from 'styled-components';

let startingX = 0;
let dragStartPoint = 0;
let startPreviewSize = 0;

const Mapping = [
    {rangeStart: 0, rangeEnd: 2000, simStart: 0, ratio: 4.44}, // up to 300 1:1
];

const realToSimulatedCords = (realInput: number) => {
    const found = Mapping.find((entry) => entry.rangeEnd > realInput);
    if (!found) {
        return realInput;
    }
    return found.simStart + (realInput - found.rangeStart) * found.ratio;
};

const simCordsToReal = (simInput: number) => {
    const found = Mapping.find((entry) => simInput < entry.rangeEnd * entry.ratio);
    if (!found) {
        return simInput;
    }
    return found.rangeStart + (simInput - found.rangeStart) / found.ratio;
};

export const Draggable: React.FC<
    React.PropsWithChildren<{previewSize: number; setPreviewSize: (newSize: number) => void}>
> = ({previewSize, setPreviewSize}) => {
    const dragging = useRef(false);
    const workingX = useRef(0);

    const {view} = useViewContext();

    const {value: minWidth} = extractNumericUnitAndValue(view.tree?.styles?.minWidth);
    const {value: maxWidth} = extractNumericUnitAndValue(view.tree?.styles?.maxWidth);

    useEffect(() => {
        const mouseMove = (evt: MouseEvent) => {
            const deltaX = evt.clientX - startingX;
            workingX.current = dragStartPoint + deltaX;
            if (!dragging.current) {
                return;
            }
            const clampedValue = _clamp(startPreviewSize + deltaX, 0, 450.5);
            const value = realToSimulatedCords(clampedValue);

            setPreviewSize(_round(_clamp(value, minWidth, maxWidth === 0 ? 2000 : maxWidth), 0));
        };
        const mouseUp = (evt: MouseEvent) => {
            dragging.current = false;
        };

        window.addEventListener('mousemove', mouseMove);
        window.addEventListener('mouseup', mouseUp);

        return () => {
            window.removeEventListener('mousemove', mouseMove);
            window.removeEventListener('mouseup', mouseUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dragging]);

    const dragStart = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        evt.preventDefault();
        dragging.current = true;
        startPreviewSize = simCordsToReal(previewSize);
        dragStartPoint = workingX.current;
        startingX = evt.clientX;
    };

    return (
        <DraggableRoot style={{marginLeft: simCordsToReal(previewSize)}} onMouseDown={(e) => dragStart(e)}>
            <Tick previewSize={previewSize}></Tick>
            <Button previewSize={previewSize}>width: {previewSize.toFixed(0)}px</Button>
        </DraggableRoot>
    );
};

const DraggableRoot = styled.div`
    cursor: grab;
    display: block;
    overflow: hidden;
    justify-content: center;
    align-items: center;
    width: 100px;
    border-width: 0px;
    text-align: center;
    box-sizing: content-box;
`;

const Tick = styled.div<{previewSize: number}>`
    height: 20px;
    margin-left: 45%;
    width: 4%;
    border-radius: 3px 3px 0px 0px;
    background: ${({previewSize}) =>
        previewSize > 768 && previewSize <= 2001
            ? 'rgba(255, 165, 0, .5)'
            : previewSize > 360 && previewSize <= 768
            ? 'rgba(238, 130, 238, .5)'
            : previewSize >= 0
            ? 'rgba(0, 0, 255, .5)'
            : 'rgba(255, 193, 31, .5)'};
    &: hover {
        box-shadow: inset 0 0 100px 100px rgba(255, 255, 255, 0.3);
    }
`;

const Button = styled.div<{previewSize: number}>`
    height: 100%;
    background: ${({previewSize}) =>
        previewSize > 768 && previewSize <= 2001
            ? 'rgba(255, 165, 0, 1)'
            : previewSize > 360 && previewSize <= 768
            ? 'rgba(238, 130, 238, 1)'
            : previewSize >= 0
            ? 'rgba(0, 150, 255, 1)'
            : 'rgba(255, 193, 31, 1)'};
    border-radius: 8px;
    padding: 10px;
    margin: auto;
    font-size: 11px;
    font-family: Inter;
    font-weight: 400;
    &: hover {
        box-shadow: inset 0 0 100px 100px rgba(255, 255, 255, 0.3);
    }
`;
